<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="cards-page">
            <!-- 使用提示 -->
            <div class="usage-tips">
                <div class="tips-header">
                    <van-icon name="warning-o" class="warning-icon" />
                    <span class="tips-title">使用提示</span>
                </div>
                <div class="tips-content">
                    {{ recharge_notice }}
                </div>
                <div class="tips-action" @click="goToFaceInput">
                    点击前往录入<van-image :src="require('@/assets/icons/click.svg')" class="click-icon" />
                </div>
            </div>

            <!-- 当前卡片 -->
            <div class="cards-section">
                <div class="section-header">
                    <span class="section-title">当前卡片</span>
                    <van-button class="add-card-btn" type="primary" plain @click="addCard">+ 添加卡片</van-button>
                </div>
                <!-- 空状态 -->
                <div v-if="cards.length === 0" class="empty-state">
                    <van-empty description="暂无卡片" />
                </div>
                <!-- 卡片列表 -->
                <div v-else>
                    <div class="cards-list">
                        <div class="card-item" v-for="card in cards" :key="card.card_id" @click="viewCardDetail(card)">
                            <div class="card-header">
                                <span class="card-type">{{ card.card_name }}</span>
                                <span class="card-remaining" v-if="card.card_type == '金额卡'">剩余：¥{{ card.card_balance
                                    }}</span>
                                <span class="card-remaining" v-if="card.card_type == '瓶数卡'">剩余：{{ card.surplus_number
                                    }}瓶</span>
                            </div>
                            <div class="card-number">{{ card.card_no }}</div>
                            <div class="card-footer">
                                <span class="card-validity">有效期至:{{ card.expiration_date }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 查看历史卡片 -->
            <div class="history-section">
                <van-button class="history-btn" @click="viewHistory">查看已完成的卡片</van-button>
            </div>

            <!-- 添加卡片弹窗 -->
            <van-dialog v-model:show="showAddCardDialog" title="添加卡片" :show-cancel-button="false"
                :show-confirm-button="false" class="add-card-dialog">
                <div class="add-card-form">
                    <div class="form-item">
                        <label class="form-label">卡号</label>
                        <van-field v-model="addCardForm.cardNumber" placeholder="请输入卡号" class="form-input" />
                    </div>
                    <div class="form-item">
                        <label class="form-label">密码</label>
                        <van-field v-model="addCardForm.password" type="password" placeholder="请输入卡片密码"
                            class="form-input" />
                    </div>
                    <div class="form-actions">
                        <van-button class="cancel-btn" @click="showAddCardDialog = false">
                            取消
                        </van-button>
                        <van-button type="primary" class="confirm-btn" @click="confirmAddCard">
                            确认
                        </van-button>
                    </div>
                </div>
            </van-dialog>
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { post } from '@/utils/request'
import { WECHAT_APPID } from '../../../config/env'
import { Toast, Dialog } from 'vant'

// 加载状态
const loading = ref(true)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 控制添加卡片弹窗显示
const showAddCardDialog = ref(false)

// 添加卡片表单数据
const addCardForm = ref({
    cardNumber: '',
    password: ''
})

// 卡片数据
const cards = ref([])

// 使用提示
const recharge_notice = ref('')

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

onMounted(() => {
    setWxTitle('我的卡片')
    initPage()
})

// 初始化微信授权
const initWechatAuth = () => {
    // 获取当前url中的code参数
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')

    // 判断用户取消授权
    const error = urlParams.get('error')
    const errorDesc = urlParams.get('error_description')
    if (error) {
        console.log('用户取消授权:', errorDesc)
        handleError('获取用户信息失败', errorDesc)
        return
    }

    if (!code) {
        // 如果没有code，重定向到微信授权页面
        const redirectUri = encodeURIComponent(window.location.href)
        const appId = WECHAT_APPID
        window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=STATE#wechat_redirect`
    } else {
        // 有code则获取用户信息
        getUserInfo({
            code: code,
            app_id: WECHAT_APPID,
            page_type: '首页'
        })
    }
}

// 获取用户信息
const getUserInfo = async (data) => {
    try {
        const res = await post('/api/index/info', data)

        if (res.result && res.result.state === 'ok') {
            const userInfo = res.result.partner_info || {}
            const accountInfo = res.result.account_info || {}
            // 存储用户ID到本地存储
            if (userInfo.id) {
                localStorage.setItem('userId', userInfo.id)
            }
            if (accountInfo.company_ids) {
                localStorage.setItem('company_ids', JSON.stringify(accountInfo.company_ids))
            }
            if (accountInfo.user_default_image) {
                localStorage.setItem('user_default_image', accountInfo.user_default_image)
            }
            initPage()
        } else {
            handleError('获取用户信息失败', res.result?.message || '获取用户信息失败')
        }
    } catch (error) {
        console.error('获取用户信息失败:', error)
        handleError('获取用户信息失败', '获取用户信息失败，请重试')
    } finally {
        loading.value = false
    }
}

// 页面初始化
const initPage = async () => {
    try {
        // 检查是否已有用户ID
        if (localStorage.getItem('userId')) {
            // 调用接口获取卡片信息
            const res = await post('/api/select/community/card/info', {
                partner_id: parseInt(localStorage.getItem('userId')),
                company_ids: JSON.parse(localStorage.getItem('company_ids')),
                state: '已绑定',
                app_id: WECHAT_APPID,
            })
            if (res.result && res.result.state === 'ok') {
                cards.value = res.result.card_info_arr || []
                recharge_notice.value = res.result.card_interface_prompt
            } else {
                handleError('加载失败', res.result?.message || '接口异常')
            }
            loading.value = false
        } else {
            initWechatAuth()
        }
    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
        loading.value = false
    }
}

// 跳转到人脸录入
const goToFaceInput = () => {
    console.log('跳转到人脸录入页面')
    window.location.href = '/students'
}

// 添加卡片
const addCard = () => {
    showAddCardDialog.value = true
}

// 确认添加卡片
const confirmAddCard = async () => {
    if (!addCardForm.value.cardNumber || !addCardForm.value.password) {
        // 可以添加表单验证提示
        Toast('请填写完整信息')
        return
    }

    try {
        // 调用API添加卡片
        const res = await post('/api/bind/card/info/partner', {
            partner_id: parseInt(localStorage.getItem('userId')),
            company_ids: JSON.parse(localStorage.getItem('company_ids')),
            card_no: addCardForm.value.cardNumber,
            card_password: addCardForm.value.password,
        })

        if (res.result && res.result.state === 'ok') {
            // 添加成功后关闭弹窗并清空表单
            showAddCardDialog.value = false
            addCardForm.value = {
                cardNumber: '',
                password: ''
            }
            // 重新加载卡片列表
            initPage()
        } else {
            console.error('添加卡片失败:', res.result?.message)
            Dialog.alert({
                title: '添加卡片失败',
                message: res.result?.message || '接口异常'
            })
        }
    } catch (error) {
        console.error('添加卡片失败:', error)
    }
}

// 查看历史卡片
const viewHistory = () => {
    window.location.href = '/completed-cards'
}

// 查看卡片详情
const viewCardDetail = (card) => {
    console.log('查看卡片详情:', card)
    window.location.href = '/card-detail?card_id=' + card.card_id
}
</script>

<style scoped>
.cards-page {
    min-height: 100vh;
    background: #fff;
    padding: 0 16px;
}

.usage-tips {
    border-radius: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    background: #fff;
    padding: 16px;
    margin-bottom: 16px;
}

.tips-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
}

.warning-icon {
    color: var(--van-primary-color);
    font-size: 16px;
}

.tips-title {
    font-size: 15px;
    font-weight: 500;
    color: #000;
}

.tips-content {
    font-size: 14px;
    color: #333;
    line-height: 1.5;
    margin-bottom: 6px;
}

.tips-action {
    color: var(--van-primary-color);
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.click-icon {
    width: 18px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.section-title {
    font-size: 18px;
    font-weight: 500;
    color: #000;
}

.add-card-btn {
    border-radius: 20px;
    padding: 6px 16px;
    font-size: 14px;
    height: 32px;
}

.cards-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.card-item {
    border-radius: 8px;
    background: linear-gradient(90.00deg, rgba(139.00048828125, 0, 0, 1), rgba(185.00250244140625, 27.999000549316406, 27.999000549316406, 1) 100%);
    padding: 12px;
    color: #fff;
    position: relative;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.card-type,
.card-validity {
    font-size: 14px;
    opacity: 0.8;
}

.card-remaining {
    font-size: 14px;
}

.card-number {
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 18px;
}

.card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.history-section {
    padding: 24px 0;
}

.history-btn {
    width: 100% !important;
    background: #fff !important;
    color: #666 !important;
    border: 1px solid #ddd !important;
    border-radius: 8px !important;
    height: 44px !important;
    font-size: 16px !important;
}

/* 添加卡片弹窗样式 */
.add-card-dialog {
    border-radius: 12px;
}

.add-card-form {
    padding: 20px;
}

.form-item {
    margin-bottom: 20px;
}

.form-label {
    display: block;
    font-size: 14px;
    color: #333;
    margin-bottom: 8px;
    font-weight: 500;
}

.form-input {
    background: #f5f5f5;
    border-radius: 8px;
}

.form-input :deep(.van-field__control) {
    background: transparent;
}

.form-actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
}

.cancel-btn {
    flex: 1;
    background: #f5f5f5 !important;
    color: #666 !important;
    border: none !important;
    border-radius: 8px !important;
    height: 44px !important;
    font-size: 16px !important;
}

.confirm-btn {
    flex: 1;
    background: var(--van-primary-color) !important;
    color: #fff !important;
    border: none !important;
    border-radius: 8px !important;
    height: 44px !important;
    font-size: 16px !important;
}
</style>